'use client';

import { ComponentProps } from 'react';
import { CentText } from '@/lib/components/Text/CentText';
import { TranslatedText } from '@/lib/components/Text/TranslatedText';
import { Numeric } from '@/lib/types/common';
import { EMarketIntent, MarketIntent } from '@/lib/types/market-intent';
import { cn } from '@/lib/ui-kit/utils';

type YesNoSwitcherProps = {
  yesPrice: Numeric | null;
  noPrice: Numeric | null;
  value?: MarketIntent;
  onChange?: (value: MarketIntent) => void;
} & Omit<ComponentProps<'div'>, 'onChange'>;

export function MarketIntentSwitch({
  value = EMarketIntent.YES,
  onChange,
  yesPrice,
  noPrice,
  className,
  ...props
}: YesNoSwitcherProps) {
  const isYes = value === EMarketIntent.YES;

  return (
    <div
      className={cn(
        'relative flex h-[46px] w-full overflow-hidden rounded-[10px] bg-v2-toggle-neutral-surface',
        className,
      )}
      {...props}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 w-1/2 rounded-[10px]"
        style={{
          backgroundColor: isYes
            ? 'var(--v2-toggle-green-surface)'
            : 'var(--v2-toggle-red-surface)',
          transform: isYes ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 300ms ease-in-out, background-color 300ms ease-in-out',
        }}
      />
      <button
        type="button"
        onClick={() => onChange?.(EMarketIntent.YES)}
        className={cn(
          'relative z-10 flex flex-1 cursor-pointer select-none items-center justify-center gap-2 px-5 py-3 text-[16px] leading-[22px] whitespace-nowrap transition-colors duration-300',
          isYes ? 'font-medium text-v2-green' : 'font-semibold text-v2-text-secondary',
        )}
      >
        <span>
          <TranslatedText token="common.yes" />
          {yesPrice !== null ? (
            <>
              {' '}
              <CentText value={yesPrice} />
            </>
          ) : null}
        </span>
      </button>
      <button
        type="button"
        onClick={() => onChange?.(EMarketIntent.NO)}
        className={cn(
          'relative z-10 flex flex-1 cursor-pointer select-none items-center justify-center gap-2 px-5 py-3 text-[16px] leading-[22px] whitespace-nowrap transition-colors duration-300',
          !isYes ? 'font-medium text-red-400' : 'font-semibold text-v2-text-secondary',
        )}
      >
        <span>
          <TranslatedText token="common.no" />
          {noPrice !== null ? (
            <>
              {' '}
              <CentText value={noPrice} />
            </>
          ) : null}
        </span>
      </button>
    </div>
  );
}
